import { shellStyles } from './styles';
import {
  getBindings,
  keyLabel,
  loadBindings,
  resetBindings,
  setBinding,
  type BindableAction,
} from '../input/bindings';
import { GAMEPAD_BUTTON_ACTIONS, type InputButton } from '../input/device';
import { learningMode, loadLearningMode, setLearningMode } from '../input/learning';
import {
  audioPreference,
  loadAudioPreference,
  setAudioPreference,
} from '../audio/preferences';
import { hasEarlyAudioGesture, markEarlyAudioGesture } from '../audio';
import {
  CardWatcher,
  GoalWatcher,
  formatMatchClock,
  scoreboardText,
  type CardEvent,
  type MatchStatsSnapshot,
} from './scoreboard';
import { DEFAULT_CAMERA_SETTINGS, type CameraSettings } from '../render/camera';
import type { Match, Violence } from '../sim/match';
import { MatchPhase } from '../sim/referee';
import { SetPiece } from '../sim/setpiece';

export type ControllerSide = -1 | 0 | 1;

export interface ShellController {
  id: string;
  label: string;
  kind: 'keyboard' | 'gamepad';
  side: ControllerSide;
}

export interface ShellFixture {
  controllers: ShellController[];
  homeTeamId: number;
  awayTeamId: number;
  homeTeamName: string;
  awayTeamName: string;
  difficulty: number;
  duration: number;
  /** Which of the two matches this is — see `Violence` in the simulation. */
  violence: Violence;
}

/**
 * Re-exported so callers that only ever talk to the shell need not reach into the renderer.
 *
 * The camera page used to declare its own copy of this along with its own starting values, and
 * the two had already drifted: the sliders opened at a different framing from the one actually
 * on screen, so touching any one of them jumped the picture.
 */
export type { CameraSettings };

/**
 * How much of the play is ringed on the grass. Not a setting the original has — it marks the
 * selected player in no way at all — so the wording here is ours, not its.
 */
export type MarkerMode = 'off' | 'controlled' | 'all';

export interface ShellMatchSession {
  setPaused(paused: boolean): void;
  /**
   * This match is finished with: stop its loop, let go of its devices and empty its scene.
   *
   * The shell decides where the player goes next and may start another match straight
   * afterwards, so this has to leave the page fit to run one — it is a teardown, not a quit.
   */
  leaveMatch(): void;
  /** The figures the full-time screen puts up. Counted outside the simulation; see scoreboard.ts. */
  matchStats?(): MatchStatsSnapshot;
  setCamera?(settings: CameraSettings): void;
  setMarkers?(mode: MarkerMode): void;
  /** Frame rate, tick counts, draw calls. Off unless somebody asks for it. */
  setDiagnostics?(on: boolean): void;
  /** Reassign devices to sides without leaving the match. */
  setControllers?(controllers: ShellController[]): void;
  setKits?(homeKit: number, awayKit: number): void;
  randomizeSun?(): void;
}

export interface GameShellOptions {
  onStartMatch(fixture: ShellFixture): Promise<ShellMatchSession>;
  onExit?(): void;
}

/** Everything the display reads off a running match, once a rendered frame. */
export type HudMatchView = Pick<
  Match,
  'matchTimeMs' | 'teams' | 'phase' | 'gameOver' | 'inPlay' | 'inSetPiece' | 'setPiece'
>;

/**
 * Play is stopped for a restart and a person is on the pitch, so a person is standing still
 * whether or not the restart is his to take. `null` in open play, and in a match nobody is
 * playing.
 */
export interface RestartPromptView {
  /** Named so the caption can say which restart it is. */
  setPiece: SetPiece;
  /**
   * Which device is driving the taker, so the caption names a key or a button and not both —
   * or null when the restart is not this person's to take and he is only waiting for it.
   */
  deviceKind: 'keyboard' | 'gamepad' | null;
}

interface Club {
  id: number;
  league: number;
  name: string;
  shortName: string;
}

interface ControllerState extends ShellController {
  gamepadIndex?: number;
}

type PageRenderer = () => void;

const LEAGUES = ["KING'S LEAGUE", 'FUSSBALL LEAGUE', 'VOETBALCOMPETITIE', 'LA LIGA'];

/** Index order must match the labels in the visual-options list. */
const MARKER_MODES: MarkerMode[] = ['off', 'controlled', 'all'];

/**
 * How long the final whistle is left to breathe before the screen comes up.
 *
 * The three-blast whistle is 1.5 seconds and the crowd swells behind it. Covering the pitch
 * before that has finished turns the end of a football match into the closing of a dialog box.
 */
const FULL_TIME_PAUSE_MS = 2600;

/** How long a card banner stays up, once shown. Long enough to read a role and a team name. */
const CARD_BANNER_MS = 4000;

/** What each card is called on the banner that announces it. */
const CARD_LABELS: Record<CardEvent['kind'], string> = {
  yellow: 'YELLOW CARD',
  'second-yellow': 'SECOND YELLOW — RED CARD',
  red: 'RED CARD',
};

/** How long a goal banner stays up, once shown. Same duration as the card banner gets. */
const GOAL_BANNER_MS = 4000;

/** What each restart is called on the caption that asks a person to take it. */
const RESTART_NAMES: Record<SetPiece, string> = {
  [SetPiece.None]: 'RESTART',
  [SetPiece.KickOff]: 'KICK-OFF',
  [SetPiece.GoalKick]: 'GOAL KICK',
  [SetPiece.FreeKick]: 'FREE KICK',
  [SetPiece.Corner]: 'CORNER',
  [SetPiece.ThrowIn]: 'THROW-IN',
  [SetPiece.Penalty]: 'PENALTY',
};

/** A whole football clock. Anything short of it and the referee abandoned the match. */
const NINETY_MINUTES_MS = 5_400_000;

/**
 * What to call the break before each period, keyed by the period about to be played, and the
 * whole of the shell's knowledge of which periods there are. There is deliberately no entry
 * for the first half: it is only ever entered from the pre-match phase, which is the match
 * starting rather than a period ending. Everything below the second half is unreachable while
 * extra time is switched off (see `Referee.extraTime`), and costs nothing to keep right.
 */
const INTERVAL_TITLES: Partial<Record<MatchPhase, string>> = {
  [MatchPhase.SecondHalf]: 'HALF TIME',
  [MatchPhase.FirstExtraTime]: 'FULL TIME · EXTRA TIME',
  [MatchPhase.SecondExtraTime]: 'EXTRA TIME · HALF TIME',
  [MatchPhase.Penalties]: 'PENALTIES',
};

/** The rows of the full-time graphic, in the order a television broadcast shows them. */
const RESULT_STATS: Array<[string, (line: MatchStatsSnapshot['home']) => number, boolean]> = [
  ['POSSESSION', (line) => line.possession, true],
  ['SHOTS', (line) => line.shots, false],
  ['ON TARGET', (line) => line.shotsOnTarget, false],
  ['CORNERS', (line) => line.corners, false],
];

const CLUBS: Club[] = [
  { id: 1, league: 2, name: 'MASTERDAM FC', shortName: 'AJA' },
  { id: 2, league: 0, name: 'GUNNERS', shortName: 'ARS' },
  { id: 3, league: 3, name: 'FC CATALUÑA', shortName: 'FCB' },
  { id: 4, league: 1, name: 'FC BAVARIA', shortName: 'BAY' },
  { id: 5, league: 1, name: 'GELBEN', shortName: 'DRT' },
  { id: 6, league: 0, name: 'RED DEVILS FC', shortName: 'UTD' },
  { id: 7, league: 2, name: 'ROOD WIT EINDHOVEN', shortName: 'PSV' },
  { id: 8, league: 3, name: 'GALACTICOS CF', shortName: 'RMD' },
];

const GAMEPLAY_SETTINGS = [
  ['SHORT PASS DIRECTION ASSIST', 0.4],
  ['SHORT PASS POWER ASSIST', 0.7],
  ['THROUGH PASS DIRECTION ASSIST', 0.2],
  ['THROUGH PASS POWER ASSIST', 0.7],
  ['HIGH PASS DIRECTION ASSIST', 0.2],
  ['HIGH PASS POWER ASSIST', 0.5],
  ['SHOT DIRECTION ASSIST', 0.2],
  ['HUMAN AGILITY FACTOR', 0.5],
  ['HUMAN ACCELERATION FACTOR', 0.5],
  ['8-WAY QUANTIZATION BIAS', 0],
] as const;

const TACTICS = [
  'ATTACKING: TEAM DEPTH',
  'DEFENDING: TEAM DEPTH',
  'ATTACKING: TEAM WIDTH',
  'DEFENDING: TEAM WIDTH',
  'ATTACKING: MIDFIELD JOINS ATTACK',
  'DEFENDING: MIDFIELD STAYS HIGH UP',
  'ATTACKING: FORWARD DRIVE',
  'DEFENDING: BACKWARD DRIVE',
  'ATTACKING: COMPACTNESS AROUND BALL',
  'DEFENDING: COMPACTNESS AROUND BALL',
  'CPU PLAYER ON THE BALL: OFFENSIVENESS',
  'CPU PLAYER ON THE BALL: PREFER CENTER',
];

function button(label: string, onClick: () => void, className = ''): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = `gf-button ${className}`.trim();
  element.textContent = label;
  element.addEventListener('click', onClick);
  return element;
}

/**
 * What every key actually does, grouped by when it does it.
 *
 * THE LABELS ON THE REBINDING SCREEN ARE NOT THIS. They name a button, once, as though it did
 * one thing all match — and half of them do not. The same physical key passes when your side
 * has the ball and closes down when it does not (`possessionContext` in
 * `src/input/player.ts`), the through-ball and shot keys are simply dead while defending
 * (`captureAction` only accepts the high-pass key then), and a set-piece taker may not move at
 * all. Somebody pressing SHOT while defending and getting nothing has been told by every
 * screen in the game that the key shoots.
 *
 * Each row carries the action whose key it shows, and that key is also the button that changes
 * it — this list is the rebinding screen as well as the explanation. `action` is null on the
 * one row that stands for several keys at once, which names them in `group` instead.
 */
const CONTROL_GROUPS: readonly {
  title: string;
  note?: string;
  rows: readonly {
    action: (BindableAction & InputButton) | null;
    what: string;
    group?: readonly BindableAction[];
  }[];
}[] = [
  {
    title: 'ALWAYS',
    rows: [
      { action: null, what: 'RUN', group: ['up', 'left', 'down', 'right'] },
      { action: 'sprint', what: 'SPRINT (HOLD)' },
      { action: 'dribble', what: 'SLOW DOWN AND KEEP IT CLOSE (HOLD)' },
      { action: 'switch', what: 'CHANGE PLAYER' },
      { action: 'teamPressure', what: 'SEND A TEAM-MATE AT THE BALL (HOLD)' },
      { action: 'keeperRush', what: 'SEND THE GOALKEEPER OUT (HOLD)' },
      { action: 'allOut', what: 'ALL OUT — YOUR TEN GO IN HARDER AND MORE OFTEN (HOLD)' },
    ],
  },
  {
    title: 'THINGS THE REFEREE WILL PUNISH',
    rows: [
      { action: 'shirtPull', what: 'HOLD HIS SHIRT AND DRAG HIM BACK (HOLD) — FREE KICK, AND A BOOKING IF HE WAS CLEAR' },
      { action: 'flyingKick', what: 'FLYING KICK AT THE NEAREST MAN — NEEDS A RUN AT HIM. A STRAIGHT RED IF IT LANDS' },
      { action: 'punch', what: 'PUNCH THE NEAREST MAN — HE PULLS UP TO SWING. A STRAIGHT RED' },
    ],
  },
  {
    title: 'WHEN YOUR SIDE HAS THE BALL',
    note: 'HOLD FOR MORE POWER. THE DIRECTION YOU ARE PRESSING AIMS IT.',
    rows: [
      { action: 'shortPass', what: 'SHORT PASS' },
      { action: 'longPass', what: 'THROUGH PASS' },
      { action: 'highPass', what: 'HIGH PASS OR CROSS' },
      { action: 'shot', what: 'SHOT' },
      { action: 'special', what: 'KNOCK IT AHEAD AND CHASE IT (HOLD)' },
      { action: 'feint', what: 'BODY SWERVE — HE SLOWS TO DRIBBLING PACE AND THROWS ONE OFF THE FOOT HE IS ON' },
      { action: 'shield', what: 'SHIELD THE BALL (HOLD) — HE STOPS, TURNS HIS BACK ON THE NEAREST MAN AND HOLDS HIM OFF' },
      { action: 'chip', what: 'CHIP THE KEEPER (HOLD) — OVER HIM AND UNDER HIS OWN BAR. NOT AT A SPRINT, AND THIS IS THE ONE KEY THAT IGNORES HOW LONG YOU HOLD IT' },
    ],
  },
  {
    title: 'WHEN IT DOES NOT',
    note: 'THE THROUGH-PASS AND SHOT KEYS DO NOTHING AT ALL WHILE YOU ARE DEFENDING.',
    rows: [
      { action: 'highPass', what: 'SLIDING TACKLE' },
      { action: 'shortPass', what: 'CLOSE THE MAN DOWN (HOLD)' },
    ],
  },
  {
    title: 'AT A RESTART THAT IS YOURS TO TAKE',
    note: 'YOU CANNOT WALK UNTIL THE BALL IS PLAYED. NOBODY ELSE ON THE PITCH CAN MOVE EITHER.',
    rows: [{ action: 'shortPass', what: 'PLAY THE BALL AND START THE MATCH' }],
  },
];

/**
 * Whether a row's action still exists at all once the device is a gamepad rather than a
 * keyboard. Checked against device.ts's own `GAMEPAD_BUTTON_ACTIONS` rather than a list typed
 * out here a second time — that second list was the bug: it named three of the seven
 * keyboard-only actions and went stale the moment the other four were added.
 */
function isOnGamepad(action: BindableAction & InputButton): boolean {
  return GAMEPAD_BUTTON_ACTIONS.has(action);
}

/**
 * The one-line reminder of the keys, on the screen where devices are assigned.
 *
 * Read off the live bindings rather than typed out. It was typed out, and so it went on naming
 * the factory keys after somebody had rebound them — telling a person to press a key that now
 * does nothing, on the very screen where he sets his controls up.
 */
function keyboardSummary(): string {
  const bindings = getBindings();
  const movement = [bindings.up, bindings.left, bindings.down, bindings.right];
  const parts = [
    // Four separate keys, so "ARROWS" is only honest while all four still are the arrows.
    `${movement.every((code) => code.startsWith('Arrow')) ? 'ARROWS' : movement.map(keyLabel).join('/')} MOVE`,
    `${keyLabel(bindings.shortPass)} PASS/PRESS`,
    `${keyLabel(bindings.longPass)} THROUGH`,
    `${keyLabel(bindings.highPass)} LOB/SLIDE`,
    `${keyLabel(bindings.shot)} SHOOT`,
    `${keyLabel(bindings.switch)} SWITCH`,
    `${keyLabel(bindings.sprint)} SPRINT`,
    `${keyLabel(bindings.dribble)} DRIBBLE`,
    `${keyLabel(bindings.special)} LONG TOUCH`,
  ];
  return `KEYBOARD: ${parts.join(' · ')}`;
}

function heading(title: string, kicker?: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  if (kicker) {
    const small = document.createElement('p');
    small.className = 'gf-kicker';
    small.textContent = kicker;
    fragment.appendChild(small);
  }
  const element = document.createElement('h1');
  element.className = 'gf-title';
  element.textContent = title;
  fragment.appendChild(element);
  return fragment;
}

function modulo(value: number, length: number): number {
  return ((value % length) + length) % length;
}

export class GameShell {
  private options: GameShellOptions;
  private root: HTMLDivElement;
  private viewport: HTMLDivElement;
  private hud: HTMLDivElement;
  private scoreboard: HTMLDivElement;
  private scoreboardClock: HTMLSpanElement;
  private scoreboardScore: HTMLSpanElement;
  private homeShortName = 'HOM';
  private awayShortName = 'AWY';
  private homeTeamName = 'HOME';
  private awayTeamName = 'AWAY';
  private pauseLayer: HTMLDivElement;
  private pausePanel: HTMLDivElement;
  /** The half-time caption over the pitch. */
  private intervalBanner: HTMLDivElement;
  private restartPrompt: HTMLDivElement;
  /** What the restart caption currently says, so an unchanged one is not rewritten every frame. */
  private restartPromptText = '';
  /** A caution or dismissal just shown, named and coloured, above the restart caption. */
  private cardBanner: HTMLDivElement;
  /** When to hide the card banner, on the page's own clock — `performance.now()`. */
  private cardBannerHideAtMs = 0;
  /** Watches every player's own card count so a new one can be announced. See `scoreboard.ts`. */
  private readonly cardWatcher = new CardWatcher();
  /** A goal just given, named by side. */
  private goalBanner: HTMLDivElement;
  /** When to hide the goal banner, on the page's own clock — `performance.now()`. */
  private goalBannerHideAtMs = 0;
  /** Watches each side's own score so a new goal can be announced. See `scoreboard.ts`. */
  private readonly goalWatcher = new GoalWatcher();
  /** The learning caption: what the button just pressed did. */
  private learningHint: HTMLDivElement;
  private learningHintText = '';
  /**
   * The boot gate: blocks the main menu until a key, click, tap or gamepad button dismisses it.
   * A gamepad button can never itself satisfy a browser's audio-unlock gesture requirement, so
   * dismissing it that way leaves `audioHint` up instead of claiming sound is ready.
   */
  private audioGate: HTMLDivElement;
  /** "Sound is off" — shown only once the boot gate was dismissed by a gamepad, not a real gesture. */
  private audioHint: HTMLDivElement;
  /** The full-time screen. */
  private resultLayer: HTMLDivElement;
  private resultPanel: HTMLDivElement;
  /**
   * When the referee blew for full time, on the page's own clock, or null while a match is
   * still being played. It is also the shell's whole notion of "this match is over": the pause
   * menu stops answering, and the result screen goes up a couple of seconds later.
   */
  private fullTimeAtMs: number | null = null;
  /** The period the display last saw, so the tick one ends on can be recognised. */
  private hudPhase: MatchPhase | null = null;
  private currentPage: PageRenderer | null = null;
  private history: PageRenderer[] = [];
  private pageId = '';
  private controllers: ControllerState[] = [];
  private homeLeague = 3;
  private awayLeague = 3;
  private homeClub = 3;
  private awayClub = 8;
  /**
   * How each choice on the current screen redraws its own name. Emptied whenever a screen is
   * built; see `carousel`, which is the only thing that fills it.
   */
  private choiceRefreshers: Array<() => void> = [];
  private difficulty = 0.6;
  private violence: Violence = 'match';
  /**
   * How long a match lasts, on the same 0..1 scale the slider on the match options screen
   * uses: 0 is five minutes of real time for a full ninety, 1 is twenty-five.
   *
   * A DELIBERATE DIVERGENCE, and the reasoning is easy to get wrong. `match.cpp:52` reads the
   * setting with a fallback of 1.0, which looks like the original's default and is not: the
   * match options screen always runs first, offers 0.4 (`gamedefines.hpp:39`) and writes the
   * slider's value to the configuration file before any match is created
   * (`matchoptions.cpp:31,56`). So a menu-started original match is 0.4 — about twelve real
   * minutes — and the 1.0 fallback is only ever reached by its quick-start debug path.
   *
   * We run at 1.0 anyway. Measured over whole matches, twelve real minutes yields about 0.8
   * goals against roughly 2.7 in real football, and the shortfall is the clock rather than the
   * football: per minute actually played the game already produces more shots than real
   * football does. Under this project's fidelity policy that is a demonstrated improvement to
   * keep rather than a parity gap to close. The slider still reaches the original's 0.4.
   */
  private duration = 1.0;
  private session: ShellMatchSession | null = null;
  private paused = false;
  private homeKit = 1;
  private awayKit = 2;
  private camera: CameraSettings = { ...DEFAULT_CAMERA_SETTINGS };
  private markers: MarkerMode = 'all';
  /**
   * Frame rate, tick counts, draw calls and the graphics card.
   *
   * OFF, because it used to be on from the first frame with no way to dismiss it, and it
   * covers a quarter of the pitch. It is a developer's readout on somebody else's football
   * match. Turned on from VISUAL OPTIONS when it is actually wanted.
   */
  private diagnostics = false;
  /** Whichever element currently carries the selection mark; see `markFocus`. */
  private focusMarked: HTMLElement | null = null;
  private previousGamepadButtons = new Map<number, boolean[]>();
  private lastAxisMove = 0;
  /**
   * Retires the window-level keydown listener a pending key rebind installs (see
   * showKeyboardSettings). The original never needs this: its rebind widget is a modal
   * overlay that takes focus and owns the keyboard until it resolves (Gui2CaptureKey,
   * settings.cpp:385-420, `KeyboardPage::SetKey`/`SetKeyDone`). There is no modal here, so
   * leaving the screen — or starting a second rebind — has to retire the first listener by
   * hand, or it keeps listening after the button that created it is gone.
   */
  private cancelKeyCapture: (() => void) | null = null;

  constructor(options: GameShellOptions) {
    // Whatever this player last set his keys to, before anything reads them.
    loadBindings();
    loadLearningMode();
    loadAudioPreference();
    this.options = options;
    this.root = document.createElement('div');
    this.viewport = document.createElement('div');
    this.hud = document.createElement('div');
    this.scoreboard = document.createElement('div');
    this.scoreboardClock = document.createElement('span');
    this.scoreboardClock.className = 'gf-scoreboard__clock';
    this.scoreboardScore = document.createElement('span');
    this.pauseLayer = document.createElement('div');
    this.pausePanel = document.createElement('div');
    this.intervalBanner = document.createElement('div');
    this.restartPrompt = document.createElement('div');
    this.cardBanner = document.createElement('div');
    this.goalBanner = document.createElement('div');
    this.learningHint = document.createElement('div');
    this.resultLayer = document.createElement('div');
    this.resultPanel = document.createElement('div');
    this.audioGate = document.createElement('div');
    this.audioHint = document.createElement('div');

    this.controllers.push({
      id: 'keyboard-0',
      label: 'KEYBOARD',
      kind: 'keyboard',
      side: -1,
    });

    this.install();
  }

  start(): void {
    if (hasEarlyAudioGesture()) this.showMainMenu();
    else this.audioGate.hidden = false;
    requestAnimationFrame(() => this.pollGamepads());
  }

  /**
   * A real gesture (caught globally by `handleKeyDown` or the `pointerdown` listener in
   * `install`) or a gamepad button dismisses the gate. Only the former can actually satisfy a
   * browser's audio-unlock requirement — see `hasEarlyAudioGesture` — so a gamepad dismissal
   * leaves the "sound is off" hint up instead of silently claiming the gate fixed it.
   */
  private dismissAudioGate(): void {
    if (this.audioGate.hidden) return;
    this.audioGate.hidden = true;
    if (!hasEarlyAudioGesture()) this.audioHint.hidden = false;
    this.showMainMenu();
  }

  private install(): void {
    if (!document.getElementById('gf-shell-styles')) {
      const style = document.createElement('style');
      style.id = 'gf-shell-styles';
      style.textContent = shellStyles;
      document.head.appendChild(style);
    }

    this.root.className = 'gf-shell';
    this.root.setAttribute('aria-live', 'polite');
    this.viewport.className = 'gf-shell__viewport';
    this.root.appendChild(this.viewport);

    this.hud.className = 'gf-hud';
    this.hud.hidden = true;
    this.scoreboard.className = 'gf-scoreboard';
    this.hud.appendChild(this.scoreboard);

    this.intervalBanner.className = 'gf-interval';
    this.intervalBanner.hidden = true;
    this.hud.appendChild(this.intervalBanner);

    this.restartPrompt.className = 'gf-restart';
    this.restartPrompt.hidden = true;
    this.hud.appendChild(this.restartPrompt);

    this.cardBanner.className = 'gf-card';
    this.cardBanner.hidden = true;
    this.hud.appendChild(this.cardBanner);

    this.goalBanner.className = 'gf-goal';
    this.goalBanner.hidden = true;
    this.hud.appendChild(this.goalBanner);

    this.learningHint.className = 'gf-learning';
    this.learningHint.hidden = true;
    this.hud.appendChild(this.learningHint);

    this.pauseLayer.className = 'gf-pause-layer';
    this.pauseLayer.hidden = true;
    this.pausePanel.className = 'gf-pause-panel';
    this.pauseLayer.appendChild(this.pausePanel);
    this.hud.appendChild(this.pauseLayer);

    this.resultLayer.className = 'gf-result-layer';
    this.resultLayer.hidden = true;
    this.resultPanel.className = 'gf-result';
    this.resultLayer.appendChild(this.resultPanel);
    this.hud.appendChild(this.resultLayer);

    this.audioGate.className = 'gf-audio-gate';
    this.audioGate.hidden = true;
    const gatePlate = document.createElement('div');
    gatePlate.className = 'gf-audio-gate__plate';
    const gateTitle = document.createElement('div');
    gateTitle.className = 'gf-audio-gate__title';
    gateTitle.textContent = 'PRESS ANY KEY OR CLICK TO BEGIN';
    const gateNote = document.createElement('div');
    gateNote.className = 'gf-audio-gate__note';
    gateNote.textContent = "A CONTROLLER BUTTON ALSO CONTINUES, BUT WON'T TURN ON SOUND BY ITSELF";
    gatePlate.append(gateTitle, gateNote);
    this.audioGate.appendChild(gatePlate);

    this.audioHint.className = 'gf-audio-hint';
    this.audioHint.hidden = true;
    const hintPlate = document.createElement('div');
    hintPlate.className = 'gf-audio-hint__plate';
    hintPlate.textContent = 'SOUND IS OFF — PRESS ANY KEY OR CLICK TO TURN IT ON';
    this.audioHint.appendChild(hintPlate);

    document.body.append(this.hud, this.root, this.audioGate, this.audioHint);
    window.addEventListener('keydown', (event) => this.handleKeyDown(event));
    window.addEventListener('focusin', (event) => this.markFocus(event.target));
    // Never removed: this is the whole page's lifetime record of "has a real gesture happened
    // yet", read by `src/main.ts` so a match started well after the boot gate can unlock sound
    // immediately instead of waiting on a second gesture a gamepad-only session may not give.
    window.addEventListener(
      'pointerdown',
      () => {
        markEarlyAudioGesture();
        this.audioHint.hidden = true;
        this.dismissAudioGate();
      },
      { capture: true },
    );
  }

  /**
   * Mark the selected item, as well as leaving it to `:focus`.
   *
   * The selection is a bar of the accent colour down the left edge of the row, drawn on
   * `:focus` — and a browser stops matching `:focus` the moment its own window goes to the
   * background. So switching to another window and back left a menu with nothing selected on
   * it, while the arrow keys carried on from wherever the cursor really was. The stylesheet
   * answers to either this attribute or `:focus`, so a mouse and a keyboard both mark it.
   *
   * Called from everywhere the shell moves focus itself AND from `focusin`, which is the only
   * way a mouse click or a Tab press is seen. An unfocused window fires no `focusin` at all,
   * which is exactly the case this exists for.
   */
  private markFocus(target: EventTarget | null): void {
    const element = target as HTMLElement | null;
    if (element === this.focusMarked) return;
    this.focusMarked?.removeAttribute('data-focused');
    this.focusMarked = null;
    if (element && element !== document.body) {
      element.setAttribute('data-focused', 'true');
      this.focusMarked = element;
    }
  }

  private makeScreen(pageId: string): HTMLDivElement {
    this.pageId = pageId;
    this.choiceRefreshers = [];
    this.viewport.replaceChildren();
    const screen = document.createElement('div');
    screen.className = 'gf-screen';
    this.viewport.appendChild(screen);
    return screen;
  }

  private navigate(renderer: PageRenderer): void {
    this.retireKeyCapture();
    if (this.currentPage) this.history.push(this.currentPage);
    this.currentPage = renderer;
    renderer();
    this.focusFirst();
  }

  private replace(renderer: PageRenderer): void {
    this.retireKeyCapture();
    this.currentPage = renderer;
    renderer();
    this.focusFirst();
  }

  private back(): void {
    this.retireKeyCapture();
    const renderer = this.history.pop();
    if (!renderer) return;
    this.currentPage = renderer;
    renderer();
    this.focusFirst();
  }

  /**
   * Leaving the current screen, by any route (BACK, Escape, a gamepad button, a page that
   * replaces itself), retires a pending key-rebind listener so it cannot outlive the screen
   * that installed it and fire against whatever the player does next. See the field comment
   * on `cancelKeyCapture`.
   */
  private retireKeyCapture(): void {
    this.cancelKeyCapture?.();
    this.cancelKeyCapture = null;
  }

  private focusFirst(container: ParentNode = this.viewport): void {
    requestAnimationFrame(() => {
      const target = container.querySelector<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled)',
      );
      if (!target) return;
      target.focus();
      this.markFocus(target);
    });
  }

  private focusElement(element: HTMLElement): void {
    requestAnimationFrame(() => {
      element.focus();
      this.markFocus(element);
    });
  }

  /**
   * The title card, and the whole of the game's top level.
   *
   * A separate title screen used to stand in front of this one, carrying the same title and the
   * words PRESS ANY KEY, and it did nothing else: a page whose only content was permission to
   * see the next page. The title is the menu's own heading now, which is what a broadcast does
   * with its opening card.
   *
   * The three modes that do not exist are one line of text rather than four dead buttons. The
   * arrow keys skip a disabled button anyway, so all four ever did was take the space the
   * working items needed.
   */
  private showMainMenu(): void {
    this.history = [];
    this.currentPage = () => this.showMainMenu();
    const screen = this.makeScreen('main-menu');
    screen.appendChild(heading('GAMEPLAY FOOTBALL', 'PUBLIC BETA 2 · V0.2'));

    const stack = document.createElement('div');
    stack.className = 'gf-stack';
    stack.append(
      button('KICK OFF', () => this.navigate(() => this.showMatchSetup()), 'gf-button--primary'),
      button('CONTROLS', () => this.navigate(() => this.showControls())),
      button('SETTINGS', () => this.navigate(() => this.showSettings())),
      button('CREDITS', () => this.navigate(() => this.showCredits())),
      button('EXIT', () => this.navigate(() => this.showOutro())),
    );
    screen.append(
      stack,
      this.paragraph('A CUP, A LEAGUE AND THE SQUAD EDITOR ARE NOT IN THIS BUILD.'),
    );
    this.focusFirst();
  }

  private showOutro(): void {
    const screen = this.makeScreen('outro');
    screen.appendChild(heading('THANKS FOR PLAYING', 'GAMEPLAY FOOTBALL'));
    const hint = document.createElement('p');
    hint.className = 'gf-hint';
    hint.textContent = 'PRESS ANY KEY TO CLOSE THE GAME · ESCAPE GOES BACK';
    screen.append(hint, button('CLOSE', () => this.exit(), 'gf-button--primary'));
    this.focusFirst();
  }

  private exit(): void {
    if (this.options.onExit) {
      this.options.onExit();
      return;
    }
    this.root.hidden = true;
  }

  /**
   * A still credit list rather than a roll.
   *
   * It used to scroll on a twenty-five-second loop with a dozen footballs bouncing across it on
   * their own timers. A person who wants to read who made this should not have to wait for the
   * line he wants to come round again.
   */
  private showCredits(): void {
    const screen = this.makeScreen('credits');
    screen.appendChild(heading('CREDITS', 'GAMEPLAY FOOTBALL'));
    const credits = document.createElement('div');
    credits.className = 'gf-credits';
    const lines: Array<[string, string]> = [
      ['DESIGN & PROGRAMMING', 'BAS WASSINK'],
      ['ARTWORK', 'COMMUNITY-CONTRIBUTED CLUB KITS AND BADGES'],
      ['ORIGINAL ENGINE', 'GAMEPLAY FOOTBALL, APACHE 2.0'],
      ['THIS VERSION', 'A REWRITE FOR THE BROWSER, IN TYPESCRIPT'],
    ];
    for (const [role, who] of lines) {
      const row = document.createElement('div');
      row.textContent = role;
      const name = document.createElement('div');
      name.className = 'gf-credits__name';
      name.textContent = who;
      credits.append(row, name);
    }
    screen.append(credits, button('BACK', () => this.back()));
  }

  /**
   * Everything that can be set and is not a control, on one page.
   *
   * There were five: SETTINGS, with GAMEPLAY, CONTROLLER, GRAPHICS and AUDIO under it, and
   * three more gamepad pages under CONTROLLER. NOT ONE OF THE SLIDERS BELOW REACHES THE MATCH
   * — they did not when they had four screens to themselves either — so the page says so, and
   * it is the last item on the menu, which is where a page nobody opens twice belongs.
   *
   * Two of those pages are gone rather than merged. GRAPHICS offered a browser a list of window
   * sizes, which it already has one of. The three gamepad pages — axis calibration, button
   * mapping, function setup — showed the word AUTO against every row and did nothing at all: a
   * pad arrives through the browser already mapped to the standard layout, so there is nothing
   * for a person to calibrate and never was.
   */
  private showSettings(): void {
    const screen = this.makeScreen('settings');
    screen.appendChild(heading('SETTINGS'));
    this.buildSettings(screen, () => this.back());
  }

  private buildSettings(target: HTMLElement, onBack: () => void): void {
    const form = document.createElement('div');
    form.className = 'gf-form';
    form.appendChild(this.groupTitle('ASSISTS'));
    form.appendChild(this.paragraph('NOTHING UNDER THIS HEADING REACHES THE MATCH YET.'));
    for (const [label, factoryValue] of GAMEPLAY_SETTINGS) {
      form.appendChild(this.sliderField(label, factoryValue));
    }

    // The two audio rows DO reach the match, on the spot and whether or not one is being
    // played: both are remembered between visits and the engine listens for them itself, so
    // this page never holds a match or a sound engine to change them.
    form.appendChild(this.groupTitle('AUDIO'));
    const sound = audioPreference();
    form.append(
      this.selectField('SOUND', ['OFF', 'ON'], sound.muted ? 0 : 1, (index) => {
        setAudioPreference({ muted: index === 0 });
      }),
      // Deliberately does not switch the sound back on. Somebody who turned it off and is now
      // setting the level for next time should not have the crowd come back at him.
      this.sliderField('VOLUME', sound.volume, undefined, (value) => {
        setAudioPreference({ volume: value });
      }),
      button('BACK', onBack),
    );
    target.appendChild(form);
  }

  /**
   * What every key does, grouped by when it does it — and the key beside each line is the
   * button that changes it.
   *
   * This was two screens. One listed what the keys do and could not change them; the other
   * listed the same fourteen actions under different names, in a different order, three levels
   * into the main menu, and could change them but said nothing about what they do. A person
   * looking for "which key slides" and a person looking for "put the slide somewhere else"
   * were sent to different pages. It is one page now, reachable from the main menu and from
   * the pause panel without leaving the match.
   */
  private buildControls(target: HTMLElement, onBack: () => void): void {
    target.replaceChildren();
    target.appendChild(heading('CONTROLS'));
    const form = document.createElement('div');
    form.className = 'gf-form';

    // Every rebind button on the page, so one can be refreshed when another changes it. Taking
    // a key from an action unbinds it there (see `setBinding`), and that has to show up on a
    // row nobody is looking at.
    const binds: Array<[BindableAction, HTMLButtonElement]> = [];
    const refresh = (): void => {
      const current = getBindings();
      for (const [action, element] of binds) element.textContent = keyLabel(current[action]);
    };
    const bind = (action: BindableAction): HTMLButtonElement => {
      const element = this.bindButton(action, refresh);
      binds.push([action, element]);
      return element;
    };

    // The learning-mode switch lives here rather than with the display settings because this
    // page is where somebody who does not know the controls already is, and the caption answers
    // the same question this page does — one press at a time, while he plays, instead of in a
    // list.
    const learningRow = document.createElement('div');
    learningRow.className = 'gf-field';
    const learningLabel = document.createElement('label');
    learningLabel.textContent = 'LEARNING MODE · NAME EACH BUTTON AS YOU PRESS IT';
    const learningButton = button(learningMode() ? 'ON' : 'OFF', () => {
      learningButton.textContent = setLearningMode(!learningMode()) ? 'ON' : 'OFF';
    });
    learningRow.append(learningLabel, learningButton);
    form.appendChild(learningRow);

    for (const group of CONTROL_GROUPS) {
      form.appendChild(this.groupTitle(group.title));
      if (group.note) form.appendChild(this.paragraph(group.note));
      for (const row of group.rows) {
        const field = document.createElement('div');
        field.className = 'gf-field';
        const label = document.createElement('label');
        if (row.action !== null && !isOnGamepad(row.action)) {
          const what = document.createElement('span');
          what.textContent = row.what;
          const badge = document.createElement('span');
          badge.className = 'gf-badge';
          badge.textContent = 'NOT ON A GAMEPAD';
          label.append(what, badge);
        } else {
          label.textContent = row.what;
        }
        field.appendChild(label);
        if (row.group) {
          // The four movement keys share one row: four separate rows for what a person thinks
          // of as one control is how the old rebinding screen opened.
          const keys = document.createElement('div');
          keys.className = 'gf-keys';
          for (const action of row.group) keys.appendChild(bind(action));
          field.appendChild(keys);
        } else {
          field.appendChild(bind(row.action!));
        }
        form.appendChild(field);
      }
    }

    form.append(
      this.paragraph(
        'PRESS A KEY TO CHANGE IT · ESCAPE CANCELS · CHANGES ARE REMEMBERED BETWEEN VISITS\n' +
          'A GAMEPAD USES THE SAME LAYOUT: THE BOTTOM FACE BUTTON IS THE SHORT PASS.\n' +
          'THE TWO ORDERS TO THE REST OF YOUR SIDE ARE THE STICKS PRESSED IN: ' +
          'LEFT SENDS A TEAM-MATE AT THE BALL, RIGHT SENDS THE GOALKEEPER OUT.\n' +
          'NOT ON A GAMEPAD MEANS THE STANDARD PAD HAS NO BUTTON LEFT FOR IT.',
      ),
      button('RESET TO DEFAULTS', () => {
        resetBindings();
        refresh();
      }),
      button('BACK', onBack),
    );
    target.appendChild(form);
  }

  /**
   * One key's rebind button: press it, then press the key you want.
   *
   * Only one capture may be in flight at a time. Without that, clicking a second row before
   * pressing a key for the first left two listeners on window at once and one keypress resolved
   * both — the second row took the key, and `setBinding`'s own displaced-holder logic then
   * stole it straight back off the first row, leaving it bound to nothing.
   *
   * The page is not redrawn when a key lands. It was, and that threw the player's focus back to
   * the top of a list of sixteen rows every time he set one key; the buttons are refreshed
   * where they stand instead.
   */
  private bindButton(action: BindableAction, refresh: () => void): HTMLButtonElement {
    const element = button(keyLabel(getBindings()[action]), () => {
      this.retireKeyCapture();
      element.textContent = 'PRESS…';
      const capture = (event: KeyboardEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        window.removeEventListener('keydown', capture, true);
        this.cancelKeyCapture = null;
        if (event.code !== 'Escape') setBinding(action, event.code);
        refresh();
      };
      window.addEventListener('keydown', capture, true);
      this.cancelKeyCapture = (): void => {
        window.removeEventListener('keydown', capture, true);
        refresh();
      };
    });
    return element;
  }

  private showControls(): void {
    const screen = this.makeScreen('controls');
    this.buildControls(screen, () => this.back());
  }

  private renderControls(): void {
    this.retireKeyCapture();
    this.buildControls(this.pausePanel, () => this.renderPauseHome());
    this.focusFirst(this.pausePanel);
  }

  /**
   * Everything a friendly needs, on one screen: both sides, who is driving each of them, and
   * the two settings that change how the match plays.
   *
   * It was three screens with a fourth in front of them — devices, then a club for each side
   * behind its own READY button, then difficulty and length — and nothing on any of them
   * depended on an answer given on another. Getting to a kick-off meant five pages and six
   * button presses; it is two pages and two now.
   */
  private showMatchSetup(): void {
    this.syncGamepads();
    const screen = this.makeScreen('match-setup');
    screen.appendChild(heading('TEAM SHEET', 'FRIENDLY MATCH'));

    const columns = document.createElement('div');
    columns.className = 'gf-columns';
    columns.append(this.sideBlock('home'), this.sideBlock('away'));
    screen.appendChild(columns);

    const devices = document.createElement('div');
    devices.className = 'gf-form';
    devices.appendChild(this.groupTitle('WHO IS PLAYING'));
    this.appendDeviceRows(devices, false);
    // A gamepad is invisible to a page until a button is pressed on it, so the invitation to
    // press one has to be on the screen whether or not any pad has appeared yet.
    devices.appendChild(
      this.paragraph(`PRESS A BUTTON ON A GAMEPAD TO JOIN\n${keyboardSummary()}`),
    );
    screen.appendChild(devices);

    const options = document.createElement('div');
    options.className = 'gf-form';
    options.appendChild(this.groupTitle('THE MATCH'));
    options.append(
      // Which of the two matches this is. The names have to say what a person gets, because
      // nothing on the pitch will explain it to him: on FOOTBALL the referee punishes what he
      // sees, and on ANYTHING GOES he never blows for it at all.
      this.carousel(
        'RULES',
        () => (this.violence === 'match' ? 'FOOTBALL' : 'ANYTHING GOES'),
        () => {
          this.violence = this.violence === 'match' ? 'anything-goes' : 'match';
          this.refreshChoices();
        },
      ),
      this.sliderField('DIFFICULTY', this.difficulty, undefined, (value) => {
        this.difficulty = value;
      }),
      this.sliderField(
        'LENGTH',
        this.duration,
        undefined,
        (value) => {
          this.duration = value;
        },
        (value) => `${Math.round(5 + value * 20)} MINUTES`,
      ),
    );
    screen.append(
      options,
      button('START MATCH', () => void this.startMatch(), 'gf-button--primary'),
      button('BACK', () => this.back()),
    );
  }

  private sideBlock(side: 'home' | 'away'): HTMLDivElement {
    const block = document.createElement('div');
    block.className = 'gf-side';
    const title = document.createElement('p');
    title.className = 'gf-side__title';
    title.textContent = side === 'home' ? 'HOME' : 'AWAY';
    block.append(
      title,
      this.carousel(
        'COMPETITION',
        () => LEAGUES[side === 'home' ? this.homeLeague : this.awayLeague]!,
        (delta) => this.changeLeague(side, delta),
      ),
      this.carousel(
        'CLUB',
        () => this.clubFor(side).name,
        (delta) => this.changeClub(side, delta),
      ),
    );
    return block;
  }

  /**
   * Who is driving, and for which side.
   *
   * The same rows appear before a match and inside one; `live` is the difference. Inside a
   * match the change goes straight through to it, where a device moved to the other side takes
   * over a player there on the next tick and one set to nobody hands his back to the computer
   * (`Match::UpdateControllerSetup`, match.cpp:587).
   */
  private appendDeviceRows(target: HTMLElement, live: boolean): void {
    for (const controller of this.controllers) {
      const row = document.createElement('div');
      row.className = 'gf-field';
      const label = document.createElement('label');
      label.textContent = controller.label;
      const side = button(this.sideLabel(controller.side), () => {
        controller.side = this.nextSide(controller.side);
        side.textContent = this.sideLabel(controller.side);
        if (!live) return;
        this.session?.setControllers?.(
          this.controllers.map((entry) => ({
            id: entry.id,
            label: entry.label,
            kind: entry.kind,
            side: entry.side,
          })),
        );
      });
      row.append(label, side);
      target.appendChild(row);
    }
  }

  /**
   * A choice with an arrow either side.
   *
   * The name in the middle is rewritten where it stands rather than by redrawing the screen,
   * which is what changing a club used to do: the page was rebuilt and focus went back to its
   * first control, so stepping through four clubs meant walking back down to the arrow four
   * times. Every choice on the screen registers a refresher, because changing the competition
   * changes the club under it too.
   */
  private carousel(
    label: string,
    read: () => string,
    step: (delta: number) => void,
  ): HTMLDivElement {
    const carousel = document.createElement('div');
    carousel.className = 'gf-carousel';
    const previous = button('‹', () => step(-1));
    previous.setAttribute('aria-label', `PREVIOUS ${label}`);
    const next = button('›', () => step(1));
    next.setAttribute('aria-label', `NEXT ${label}`);

    const choice = document.createElement('div');
    choice.className = 'gf-carousel__choice';
    const caption = document.createElement('span');
    caption.className = 'gf-carousel__label';
    caption.textContent = label;
    const name = document.createElement('span');
    name.textContent = read();
    choice.append(caption, name);
    this.choiceRefreshers.push(() => {
      name.textContent = read();
    });

    carousel.append(previous, choice, next);
    return carousel;
  }

  private changeLeague(side: 'home' | 'away', delta: number): void {
    if (side === 'home') {
      this.homeLeague = modulo(this.homeLeague + delta, LEAGUES.length);
      this.homeClub = CLUBS.find((club) => club.league === this.homeLeague)!.id;
    } else {
      this.awayLeague = modulo(this.awayLeague + delta, LEAGUES.length);
      this.awayClub = CLUBS.find((club) => club.league === this.awayLeague)!.id;
    }
    this.refreshChoices();
  }

  private changeClub(side: 'home' | 'away', delta: number): void {
    const league = side === 'home' ? this.homeLeague : this.awayLeague;
    const clubs = CLUBS.filter((club) => club.league === league);
    const currentId = side === 'home' ? this.homeClub : this.awayClub;
    const index = clubs.findIndex((club) => club.id === currentId);
    const next = clubs[modulo(index + delta, clubs.length)]!;
    if (side === 'home') this.homeClub = next.id;
    else this.awayClub = next.id;
    this.refreshChoices();
  }

  private refreshChoices(): void {
    for (const refresh of this.choiceRefreshers) refresh();
  }

  private clubFor(side: 'home' | 'away'): Club {
    const id = side === 'home' ? this.homeClub : this.awayClub;
    return CLUBS.find((club) => club.id === id)!;
  }

  private async startMatch(): Promise<void> {
    const home = this.clubFor('home');
    const away = this.clubFor('away');
    const fixture: ShellFixture = {
      controllers: this.controllers.map(({ id, label, kind, side }) => ({ id, label, kind, side })),
      homeTeamId: home.id,
      awayTeamId: away.id,
      homeTeamName: home.name,
      awayTeamName: away.name,
      difficulty: this.difficulty,
      duration: this.duration,
      violence: this.violence,
    };

    const screen = this.makeScreen('loading-match');
    screen.appendChild(heading('LOADING MATCH'));
    const loading = document.createElement('div');
    loading.className = 'gf-loading';
    const homeName = document.createElement('div');
    homeName.className = 'gf-loading__team';
    homeName.textContent = home.name;
    const spinner = document.createElement('div');
    spinner.className = 'gf-spinner';
    spinner.setAttribute('role', 'progressbar');
    const awayName = document.createElement('div');
    awayName.className = 'gf-loading__team';
    awayName.textContent = away.name;
    loading.append(homeName, spinner, awayName);
    screen.appendChild(loading);

    // Let the loading screen paint before the main thread disappears into asset decoding.
    // Two animation frames is the reliable way to know it is on screen — but a browser fires
    // none at all for a tab that is not visible, so a player who switches away during the
    // countdown would come back to a loading screen that never moves. The timeout is the
    // floor: waiting is a courtesy, not a precondition.
    await new Promise<void>((resolve) => {
      let settled = false;
      const done = (): void => {
        if (settled) return;
        settled = true;
        resolve();
      };
      requestAnimationFrame(() => requestAnimationFrame(done));
      setTimeout(done, 100);
    });
    try {
      this.session = await this.options.onStartMatch(fixture);
      this.enterMatch(fixture);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      screen.replaceChildren(
        heading('MATCH COULD NOT START'),
        this.paragraph(message),
        button('BACK', () => this.back()),
      );
      this.focusFirst();
    }
  }

  private enterMatch(fixture: ShellFixture): void {
    this.root.hidden = true;
    this.hud.hidden = false;
    this.homeShortName = CLUBS.find((club) => club.id === fixture.homeTeamId)?.shortName ?? 'HOM';
    this.awayShortName = CLUBS.find((club) => club.id === fixture.awayTeamId)?.shortName ?? 'AWY';
    this.homeTeamName = fixture.homeTeamName;
    this.awayTeamName = fixture.awayTeamName;
    this.scoreboardClock.textContent = '00:00';
    this.scoreboardScore.textContent = `${this.homeShortName}  0 – 0  ${this.awayShortName}`;
    this.scoreboard.replaceChildren(this.scoreboardClock, this.scoreboardScore);
    this.paused = false;
    this.fullTimeAtMs = null;
    this.hudPhase = null;
    this.intervalBanner.hidden = true;
    this.cardWatcher.reset();
    this.cardBanner.hidden = true;
    this.cardBanner.replaceChildren();
    this.cardBannerHideAtMs = 0;
    this.goalWatcher.reset();
    this.goalBanner.hidden = true;
    this.goalBanner.replaceChildren();
    this.goalBannerHideAtMs = 0;
    this.resultLayer.hidden = true;
    this.resultPanel.replaceChildren();
    this.pauseLayer.hidden = true;
    this.pausePanel.replaceChildren();
  }

  /**
   * Everything the display takes off the match, once per rendered frame, from the same
   * fixed-timestep loop that draws the pitch (`main.ts`'s `render` callback) — there is no
   * separate timer behind any of it.
   *
   * Assigning `textContent` replaces the element's text node even when the string is
   * identical, so the clock and the score are only written when they actually change: the
   * clock ticks about once a second and the score a handful of times a match, against sixty
   * frames.
   */
  updateHud(
    match: HudMatchView,
    restart: RestartPromptView | null = null,
    hint: string | null = null,
  ): void {
    const { clock, score } = scoreboardText(match, this.homeShortName, this.awayShortName);
    if (this.scoreboardClock.textContent !== clock) this.scoreboardClock.textContent = clock;
    if (this.scoreboardScore.textContent !== score) this.scoreboardScore.textContent = score;
    this.showRestartPrompt(match, restart);
    this.showCardBanner(match);
    this.showGoalBanner(match);
    this.showLearningHint(hint);
    this.followPeriods(match);
  }

  /**
   * Learning mode: one line low on the screen naming what the button just pressed actually did.
   *
   * Low and quiet on purpose. It is a hint, not the restart caption, and it must not compete
   * with the football — somebody who already knows the controls should be able to leave it on
   * and stop noticing it. Switched off from the CONTROLS page; the text itself is written by
   * `describePress` in `src/input/player.ts`, which is where the situation is known.
   */
  private showLearningHint(hint: string | null): void {
    const wanted = hint && !this.paused && !this.matchOver ? hint : '';
    if (wanted === this.learningHintText) return;
    this.learningHintText = wanted;
    if (wanted === '') {
      this.learningHint.replaceChildren();
    } else {
      const plate = document.createElement('div');
      plate.className = 'gf-learning__plate';
      plate.textContent = wanted;
      this.learningHint.replaceChildren(plate);
    }
    this.learningHint.hidden = wanted === '';
  }

  /**
   * Say why nobody is moving while play is stopped, and when it is the person's own restart,
   * name the key that starts it.
   *
   * NOBODY a person is driving may move at a restart. The taker may only play the ball, and
   * everyone else holds his position (`humanPlayerCommands` in `src/sim/controller.ts`). The
   * football clock does not advance while play is stopped either, so a person who takes the
   * kick-off — which is every person who starts a match, because the home side always kicks
   * off and the keyboard always joins on the home side — sees twenty-two men standing still, a
   * clock frozen at 00:00 and arrow keys that do nothing. It is indistinguishable from a hung
   * game: measured headlessly, twelve simulated seconds with the clock still reading zero, and
   * a single press of the pass key took the same run to thirty-four seconds.
   *
   * The key is read from the live bindings rather than written into the sentence, so rebinding
   * the pass during a match changes what this says.
   */
  private showRestartPrompt(match: HudMatchView, restart: RestartPromptView | null): void {
    let wanted = '';
    if (restart && match.inSetPiece && !match.gameOver && !this.paused) {
      const name = RESTART_NAMES[restart.setPiece];
      wanted =
        restart.deviceKind === null
          ? `${name} — WAITING FOR THE RESTART`
          : `${name} — PRESS ${
              restart.deviceKind === 'keyboard'
                ? keyLabel(getBindings().shortPass)
                : 'THE PASS BUTTON'
            } TO PLAY IT`;
    }
    if (wanted === this.restartPromptText) return;
    this.restartPromptText = wanted;
    if (wanted === '') {
      this.restartPrompt.replaceChildren();
    } else {
      const plate = document.createElement('div');
      plate.className = 'gf-restart__plate';
      plate.textContent = wanted;
      this.restartPrompt.replaceChildren(plate);
    }
    this.restartPrompt.hidden = wanted === '';
  }

  /**
   * Announce a caution or a dismissal the moment the referee shows it.
   *
   * `CardWatcher.sample` reports a new one exactly once, so all this has to decide is whether
   * to put a fresh plate up or let the current one finish out its own clock on
   * `cardBannerHideAtMs` — the same "read as a change between two frames" rule as the rest of
   * this file, not an event the simulation raised. The team name is the one already on the
   * scoreboard; the role is all the identity a `MatchPlayer` carries, since nothing in the
   * simulation gives a player a name (`sim/match.ts`).
   */
  private showCardBanner(match: HudMatchView): void {
    const events = this.cardWatcher.sample(match);
    if (events.length > 0) {
      const text = events
        .map((event) => {
          const team = event.teamIndex === 0 ? this.homeShortName : this.awayShortName;
          return `${CARD_LABELS[event.kind]} — ${team} ${event.role}`;
        })
        .join('   ·   ');
      const plate = document.createElement('div');
      plate.className = 'gf-card__plate';
      plate.textContent = text;
      this.cardBanner.replaceChildren(plate);
      this.cardBanner.classList.toggle(
        'gf-card--red',
        events.some((event) => event.kind !== 'yellow'),
      );
      this.cardBanner.hidden = false;
      this.cardBannerHideAtMs = performance.now() + CARD_BANNER_MS;
    } else if (!this.cardBanner.hidden && performance.now() >= this.cardBannerHideAtMs) {
      this.cardBanner.replaceChildren();
      this.cardBanner.hidden = true;
    }
  }

  /**
   * Announce a goal the moment the referee gives it.
   *
   * Before this, the only thing that changed anywhere on screen when the ball crossed the
   * line was the scoreboard's own score text swapping for a bigger number — no flash, no
   * colour, nothing a player who glanced away for two seconds would catch without doing
   * arithmetic on it. `GoalWatcher.sample` reports a new goal exactly once per side, the same
   * "read as a change between two frames" trick `showCardBanner` above plays on card counts.
   * There is no scorer's name on it, for the same honest reason the card banner names a role
   * rather than a person: nothing in the simulation records which player scored (`sim/match.ts`).
   */
  private showGoalBanner(match: HudMatchView): void {
    const events = this.goalWatcher.sample(match);
    if (events.length > 0) {
      const text = events
        .map((event) => `GOAL — ${event.teamIndex === 0 ? this.homeShortName : this.awayShortName}`)
        .join('   ·   ');
      const plate = document.createElement('div');
      plate.className = 'gf-goal__plate';
      plate.textContent = text;
      this.goalBanner.replaceChildren(plate);
      this.goalBanner.hidden = false;
      this.goalBannerHideAtMs = performance.now() + GOAL_BANNER_MS;
    } else if (!this.goalBanner.hidden && performance.now() >= this.goalBannerHideAtMs) {
      this.goalBanner.replaceChildren();
      this.goalBanner.hidden = true;
    }
  }

  /**
   * Watch for a period ending, and for the match ending.
   *
   * Both are read as changes between two frames rather than announced, because the simulation
   * announces nothing: a half is over when the period the referee is playing has moved on, and
   * the match is over when it says so. Full time schedules no restart at all, which is why
   * anything watching the restart — the whistles used to, and only the whistles — never heard
   * about it.
   */
  private followPeriods(match: HudMatchView): void {
    if (match.gameOver) {
      if (this.fullTimeAtMs === null) {
        this.fullTimeAtMs = performance.now();
        this.intervalBanner.hidden = true;
        // A finished match cannot be paused, and the pause menu must not be sitting over the
        // result screen when it arrives.
        if (this.paused) this.togglePause(false);
      } else if (
        this.resultLayer.hidden &&
        performance.now() - this.fullTimeAtMs >= FULL_TIME_PAUSE_MS
      ) {
        this.showFullTime(match);
      }
      return;
    }

    if (this.hudPhase === null) {
      this.hudPhase = match.phase;
      return;
    }
    if (match.phase !== this.hudPhase) {
      this.hudPhase = match.phase;
      const title = INTERVAL_TITLES[match.phase];
      if (title) this.showInterval(title, match);
    } else if (match.inPlay && !this.intervalBanner.hidden) {
      // The next period is under way — the referee has whistled and the ball is live again.
      this.intervalBanner.hidden = true;
    }
  }

  /** The score on the screen through the interval, the way a broadcast holds it there. */
  private showInterval(title: string, match: HudMatchView): void {
    const heading = document.createElement('p');
    heading.className = 'gf-interval__title';
    heading.textContent = title;
    const score = document.createElement('p');
    score.className = 'gf-interval__score';
    score.textContent =
      `${this.homeTeamName}  ${match.teams[0].score} – ${match.teams[1].score}  ${this.awayTeamName}`;
    const plate = document.createElement('div');
    plate.className = 'gf-interval__plate';
    plate.append(heading, score);
    this.intervalBanner.replaceChildren(plate);
    this.intervalBanner.hidden = false;
  }

  /**
   * The full-time screen: the result, then what a commentator would read out, then a way on.
   *
   * The original's own game-over page is a black rectangle with the score across the top and
   * two rows of numbers under it — possession and shots — and a single button back to the main
   * menu (`gameover.cpp:13-60`). The same information is here, plus shots on target and
   * corners, plus the one button its author wrote the code for and never wired up: it has a
   * working rematch path that nothing in the game can reach (`gameover.cpp:63-73`).
   */
  private showFullTime(match: HudMatchView): void {
    const home = match.teams[0].score;
    const away = match.teams[1].score;
    // A match can also end because a side has been reduced to six men, and that is not a
    // result — the referee has abandoned it. The clock is the honest way to tell them apart.
    const abandoned = match.matchTimeMs < NINETY_MINUTES_MS;

    const kicker = document.createElement('p');
    kicker.className = 'gf-kicker';
    kicker.textContent = abandoned
      ? `MATCH ABANDONED · ${formatMatchClock(match.matchTimeMs)}`
      : `FULL TIME · ${formatMatchClock(match.matchTimeMs)}`;

    const scoreLine = document.createElement('div');
    scoreLine.className = 'gf-result__score';
    const homeName = document.createElement('span');
    homeName.className = 'gf-result__team';
    homeName.textContent = this.homeTeamName;
    const goals = document.createElement('span');
    goals.className = 'gf-result__goals';
    goals.textContent = `${home} – ${away}`;
    const awayName = document.createElement('span');
    awayName.className = 'gf-result__team';
    awayName.textContent = this.awayTeamName;
    scoreLine.append(homeName, goals, awayName);

    const verdict = document.createElement('p');
    verdict.className = 'gf-result__verdict';
    verdict.textContent =
      home === away
        ? 'ALL SQUARE'
        : `${home > away ? this.homeTeamName : this.awayTeamName} WIN`;

    const actions = document.createElement('div');
    actions.className = 'gf-row';
    const again = button('PLAY AGAIN', () => this.playAgain(), 'gf-button--primary');
    actions.append(again, button('MAIN MENU', () => this.goToMainMenu()));

    const stats = this.session?.matchStats?.();
    this.resultPanel.replaceChildren(kicker, scoreLine, verdict);
    if (stats) this.resultPanel.appendChild(this.resultStats(stats));
    this.resultPanel.appendChild(actions);

    this.resultLayer.hidden = false;
    this.focusElement(again);
  }

  /** One row per figure: this side's number, what it is, and theirs — with a share bar. */
  private resultStats(stats: MatchStatsSnapshot): HTMLDivElement {
    const table = document.createElement('div');
    table.className = 'gf-result__stats';
    for (const [label, read, isPercent] of RESULT_STATS) {
      const homeValue = read(stats.home);
      const awayValue = read(stats.away);
      const row = document.createElement('div');
      row.className = 'gf-stat';

      const left = document.createElement('span');
      left.className = 'gf-stat__value';
      left.textContent = isPercent ? `${homeValue}%` : String(homeValue);
      const middle = document.createElement('span');
      middle.className = 'gf-stat__label';
      middle.textContent = label;
      const right = document.createElement('span');
      right.className = 'gf-stat__value gf-stat__value--away';
      right.textContent = isPercent ? `${awayValue}%` : String(awayValue);

      const bar = document.createElement('div');
      bar.className = 'gf-stat__bar';
      const total = homeValue + awayValue;
      const homeShare = total === 0 ? 0 : (homeValue / total) * 100;
      const homeFill = document.createElement('span');
      homeFill.style.width = `${homeShare}%`;
      const awayFill = document.createElement('span');
      awayFill.style.width = `${total === 0 ? 0 : 100 - homeShare}%`;
      bar.append(homeFill, awayFill);

      row.append(left, middle, right, bar);
      table.appendChild(row);
    }
    return table;
  }

  /** Hand the page back: the match lets go of its devices and its scene, the menus return. */
  private closeMatch(): void {
    this.session?.leaveMatch();
    this.session = null;
    this.paused = false;
    this.fullTimeAtMs = null;
    this.hudPhase = null;
    this.hud.hidden = true;
    this.intervalBanner.hidden = true;
    this.intervalBanner.replaceChildren();
    this.resultLayer.hidden = true;
    this.resultPanel.replaceChildren();
    this.pauseLayer.hidden = true;
    this.pausePanel.replaceChildren();
    this.root.hidden = false;
  }

  private goToMainMenu(): void {
    this.closeMatch();
    this.showMainMenu();
  }

  /**
   * The same two teams again, from the same screen the player started them on. Nothing is
   * carried over — a new match is built from scratch, so the seed, the formations and the
   * scoreline all begin again.
   */
  private playAgain(): void {
    this.closeMatch();
    void this.startMatch();
  }

  private get matchOver(): boolean {
    return this.fullTimeAtMs !== null;
  }

  private togglePause(force?: boolean): void {
    if (!this.session) return;
    // Nothing to pause once the referee has finished: the only menu after that is the result.
    if (this.matchOver && force !== false) return;
    const next = force ?? !this.paused;
    this.paused = next;
    this.session.setPaused(next);
    this.pauseLayer.hidden = !next;
    if (next) {
      this.renderPauseHome();
    } else {
      // A rebind left waiting for a key when the panel closed would otherwise take the first
      // key pressed in open play, which is a pass.
      this.retireKeyCapture();
      this.pausePanel.replaceChildren();
    }
  }

  /**
   * Clear the pause panel and put a heading on it.
   *
   * Every panel goes through here, which is also where a pending key rebind is retired: the
   * controls page can be opened during a match now, and a capture listener left behind by a
   * player who pressed a key button and then walked away would go on eating his next keypress
   * — during a match, where that keypress is a pass.
   */
  private pausePage(title: string): HTMLDivElement {
    this.retireKeyCapture();
    this.pausePanel.replaceChildren();
    this.pausePanel.appendChild(heading(title, 'PAUSED'));
    const stack = document.createElement('div');
    stack.className = 'gf-form';
    this.pausePanel.appendChild(stack);
    return stack;
  }

  /**
   * The pause menu.
   *
   * It had nine items over seven panels, three of which led to further panels of their own:
   * GAME PLAN held one working item between two disabled ones, SYSTEM SETTINGS repeated the
   * pre-match settings pages, CAMERA SETTINGS and VISUAL OPTIONS both changed how the picture
   * looks, and REPLAY existed to say that replays do not. What is left is what a person pauses
   * a football match to do.
   */
  private renderPauseHome(): void {
    this.retireKeyCapture();
    this.pausePanel.replaceChildren();
    this.pausePanel.appendChild(heading('PAUSED'));
    const stack = document.createElement('div');
    stack.className = 'gf-stack';
    stack.append(
      button('RESUME', () => this.togglePause(false), 'gf-button--primary'),
      button('CONTROLS', () => this.renderControls()),
      button('WHO IS PLAYING', () => this.renderIngameControllers()),
      button('PICTURE', () => this.renderPicture()),
      button('TACTICS', () => this.renderTactics()),
      button('SETTINGS', () => this.renderPauseSettings()),
      button('FORFEIT MATCH', () => this.renderForfeit(), 'gf-button--danger'),
    );
    this.pausePanel.appendChild(stack);
    this.focusFirst(this.pausePanel);
  }

  private renderIngameControllers(): void {
    const stack = this.pausePage('WHO IS PLAYING');
    this.appendDeviceRows(stack, true);
    stack.append(
      this.paragraph(
        'A DEVICE MOVED TO THE OTHER SIDE PICKS UP A PLAYER THERE ON THE NEXT TICK.\n' +
          'SET TO NOBODY, IT HANDS HIS BACK TO THE COMPUTER.',
      ),
      button('BACK', () => this.renderPauseHome()),
    );
    this.focusFirst(this.pausePanel);
  }

  private renderTactics(): void {
    const stack = this.pausePage('TACTICS');
    for (const tactic of TACTICS) stack.appendChild(this.sliderField(tactic, 0.5));
    stack.append(
      this.paragraph('NOTHING ON THIS PAGE REACHES THE MATCH YET.'),
      button('BACK', () => this.renderPauseHome()),
    );
    this.focusFirst(this.pausePanel);
  }

  /**
   * How the match is filmed and what is drawn over it, on one panel.
   *
   * Two before: CAMERA SETTINGS and VISUAL OPTIONS. Everything on both changes what is on the
   * screen this instant, and a person adjusting the camera height is the same person deciding
   * whether he wants a ring under his player.
   */
  private renderPicture(): void {
    const stack = this.pausePage('PICTURE');
    const update = (key: keyof CameraSettings) => (value: number): void => {
      this.camera[key] = value;
      this.session?.setCamera?.(this.camera);
    };
    stack.appendChild(this.groupTitle('CAMERA'));
    stack.append(
      this.sliderField('ZOOM', this.camera.zoom, undefined, update('zoom')),
      this.sliderField('HEIGHT', this.camera.height, undefined, update('height')),
      this.sliderField('FIELD OF VIEW', this.camera.fov, undefined, update('fov')),
      this.sliderField('HORIZONTAL ANGLE', this.camera.angle, undefined, update('angle')),
    );
    stack.appendChild(this.groupTitle('ON THE PITCH'));
    stack.append(
      this.selectField('HOME KIT', ['KIT 01', 'KIT 02'], this.homeKit - 1, (index) => {
        this.homeKit = index + 1;
        this.session?.setKits?.(this.homeKit, this.awayKit);
      }),
      this.selectField('AWAY KIT', ['KIT 01', 'KIT 02'], this.awayKit - 1, (index) => {
        this.awayKit = index + 1;
        this.session?.setKits?.(this.homeKit, this.awayKit);
      }),
      this.selectField(
        'PLAYER MARKERS',
        ['OFF', 'YOUR PLAYER', 'YOUR PLAYER + BALL'],
        MARKER_MODES.indexOf(this.markers),
        (index) => {
          this.markers = MARKER_MODES[index] ?? 'all';
          this.session?.setMarkers?.(this.markers);
        },
      ),
      this.selectField('DIAGNOSTICS', ['OFF', 'ON'], this.diagnostics ? 1 : 0, (index) => {
        this.diagnostics = index === 1;
        this.session?.setDiagnostics?.(this.diagnostics);
      }),
      button('RANDOMIZE SUN POSITION', () => this.session?.randomizeSun?.()),
      button('BACK', () => this.renderPauseHome()),
    );
    this.focusFirst(this.pausePanel);
  }

  private renderPauseSettings(): void {
    const stack = this.pausePage('SETTINGS');
    this.buildSettings(stack, () => this.renderPauseHome());
    this.focusFirst(this.pausePanel);
  }

  private renderForfeit(): void {
    this.retireKeyCapture();
    const continueButton = button('CONTINUE', () => this.renderPauseHome());
    this.pausePanel.replaceChildren(
      heading('FORFEIT MATCH'),
      this.paragraph('ARE YOU SURE YOU WANT TO FORFEIT?'),
      // Straight back to the main menu, where the original goes too (`ingame.cpp:143`,
      // PreQuitPage::GoQuit). This used to reload the whole page, which threw away the shell
      // along with the match and dropped the player back on the title screen.
      button('OK', () => this.goToMainMenu(), 'gf-button--danger'),
      continueButton,
    );
    // OK stays first in reading order, matching the original's own layout (okButton is added
    // to the grid before cancelButton), but default focus is the safe option, not the
    // destructive one: the original calls cancelButton->SetFocus() explicitly rather than
    // relying on layout order (ingame.cpp:116-132, PreQuitPage). A stray confirm press must
    // back out of the dialog, not end the match.
    this.focusElement(continueButton);
  }

  /** The rule and small caps that separate one block of rows from the next. */
  private groupTitle(text: string): HTMLParagraphElement {
    const title = document.createElement('p');
    title.className = 'gf-group';
    title.textContent = text;
    return title;
  }

  private sliderField(
    labelText: string,
    value: number,
    helper?: string,
    onChange?: (value: number) => void,
    format: (value: number) => string = (current) => current.toFixed(2),
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'gf-field';
    const label = document.createElement('label');
    label.textContent = helper ? `${labelText} · ${helper}` : labelText;
    const controls = document.createElement('div');
    controls.className = 'gf-slider';
    const input = document.createElement('input');
    input.type = 'range';
    input.min = '0';
    input.max = '1';
    input.step = '0.02';
    input.value = String(value);
    input.setAttribute('aria-label', labelText);
    const output = document.createElement('output');
    output.textContent = format(value);
    input.addEventListener('input', () => {
      const current = Number(input.value);
      output.textContent = format(current);
      onChange?.(current);
    });
    controls.append(input, output);
    row.append(label, controls);
    return row;
  }

  private selectField(
    labelText: string,
    values: string[],
    selected: number,
    onChange: (index: number) => void,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'gf-field';
    const label = document.createElement('label');
    label.textContent = labelText;
    const select = document.createElement('select');
    for (const [index, value] of values.entries()) {
      const option = document.createElement('option');
      option.textContent = value;
      option.selected = index === selected;
      select.appendChild(option);
    }
    select.addEventListener('change', () => onChange(select.selectedIndex));
    row.append(label, select);
    return row;
  }

  private paragraph(text: string): HTMLParagraphElement {
    const paragraph = document.createElement('p');
    paragraph.className = 'gf-note';
    paragraph.textContent = text;
    return paragraph;
  }

  /**
   * Which side a device is driving, named the way the team sheet above it names them. It said
   * TEAM 1 and TEAM 2, which are the simulation's own words for the two sides and appear
   * nowhere a person can see.
   */
  private sideLabel(side: ControllerSide): string {
    if (side === -1) return 'HOME';
    if (side === 1) return 'AWAY';
    return 'NOBODY';
  }

  private nextSide(side: ControllerSide): ControllerSide {
    if (side === -1) return 0;
    if (side === 0) return 1;
    return -1;
  }

  private handleKeyDown(event: KeyboardEvent): void {
    markEarlyAudioGesture();
    this.audioHint.hidden = true;
    // The key that wakes the boot gate is not also read as a menu command — it only opens the
    // main menu, the same as a gamepad button or a click on the gate would.
    if (!this.audioGate.hidden) {
      this.dismissAudioGate();
      return;
    }

    if (event.defaultPrevented) return;

    if (this.session && !this.root.hidden) return;
    if (this.session && this.root.hidden) {
      // The result screen answers the arrows like any other screen, but Escape has nothing
      // left to open once the referee has finished.
      if (this.matchOver) {
        this.menuKey(event);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.togglePause();
      } else if (!this.paused && event.key === 'Enter') {
        event.preventDefault();
        this.togglePause(true);
      } else if (this.paused) {
        // Only while the panel is up. The arrows are how a person runs, so eating them during
        // play would take his controls away.
        this.menuKey(event);
      }
      return;
    }

    // Any key closes the game from the farewell screen — except the one that means "back"
    // everywhere else in these menus. It used to close on Escape too, so a person who opened
    // EXIT to see what it was and pressed Escape to leave got what he was backing out of.
    if (this.pageId === 'outro' && event.key !== 'Escape') {
      event.preventDefault();
      this.exit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.back();
      return;
    }
    this.menuKey(event);
  }

  /**
   * Drive a menu from the arrow keys.
   *
   * Nothing did before this: the pad could walk the buttons and the keyboard could not, so on
   * a keyboard the only way through the menus was the mouse, or Tab, which nothing on any
   * screen said. Up and down step through the controls in reading order. Left and right do the
   * same EXCEPT on a slider or a drop-down, where the browser's own handling changes the value
   * — which is what a person expects of a row that holds a number, and the only way to set one
   * from the keyboard at all.
   */
  private menuKey(event: KeyboardEvent): void {
    // By tag name rather than by class: the test harness hand-rolls the DOM it drives the real
    // shell through, so `HTMLInputElement` does not exist there and `instanceof` throws.
    const tag = (document.activeElement as HTMLElement | null)?.tagName?.toLowerCase();
    const adjustable = tag === 'input' || tag === 'select';
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveFocus(-1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveFocus(1);
    } else if (event.key === 'ArrowLeft' && !adjustable) {
      event.preventDefault();
      this.moveFocus(-1);
    } else if (event.key === 'ArrowRight' && !adjustable) {
      event.preventDefault();
      this.moveFocus(1);
    }
  }

  private syncGamepads(): void {
    if (!navigator.getGamepads) return;
    for (const gamepad of navigator.getGamepads()) {
      if (!gamepad || this.controllers.some((controller) => controller.gamepadIndex === gamepad.index)) {
        continue;
      }
      const pressed = gamepad.buttons.some((entry) => entry.pressed);
      if (!pressed) continue;
      this.controllers.push({
        id: `gamepad-${gamepad.index}`,
        label: gamepad.id || `GAMEPAD ${gamepad.index + 1}`,
        kind: 'gamepad',
        side: 0,
        gamepadIndex: gamepad.index,
      });
    }
  }

  private pollGamepads(): void {
    this.pollGamepadsOnce();
    requestAnimationFrame(() => this.pollGamepads());
  }

  /** Split out from `pollGamepads` so a test can drive one poll without a real animation frame. */
  private pollGamepadsOnce(): void {
    if (!this.audioGate.hidden) {
      if (navigator.getGamepads) {
        for (const gamepad of navigator.getGamepads()) {
          if (gamepad?.buttons.some((button) => button.pressed)) {
            this.dismissAudioGate();
            break;
          }
        }
      }
      return;
    }

    if (navigator.getGamepads) {
      const before = this.controllers.length;
      this.syncGamepads();
      // A pad that has just announced itself has to appear in the list of who is playing, and
      // the setup screen is the only screen that carries one.
      if (before !== this.controllers.length && this.pageId === 'match-setup') {
        this.replace(() => this.showMatchSetup());
      }

      const now = performance.now();
      for (const gamepad of navigator.getGamepads()) {
        if (!gamepad) continue;
        const previous = this.previousGamepadButtons.get(gamepad.index) ?? [];
        const current = gamepad.buttons.map((entry) => entry.pressed);
        // A finished match falls straight through to the ordinary menu handling below, so the
        // result screen can be driven on a pad like every other screen in the game.
        if (this.session && this.root.hidden && !this.matchOver) {
          if (current[9] && !previous[9]) this.togglePause();
          if (!this.paused) {
            this.previousGamepadButtons.set(gamepad.index, current);
            continue;
          }
        }
        if (current[0] && !previous[0]) this.activateFocused();
        // Cancel does nothing on the result screen: the match behind it is over, and the
        // menus behind that are not where a stray press should land the player.
        if (current[1] && !previous[1] && !this.matchOver) {
          if (this.session && this.paused) this.togglePause(false);
          else this.back();
        }
        const horizontal = gamepad.axes[0] ?? 0;
        const vertical = gamepad.axes[1] ?? 0;
        if (now - this.lastAxisMove > 180 && (Math.abs(horizontal) > 0.6 || Math.abs(vertical) > 0.6)) {
          const dominant = Math.abs(horizontal) > Math.abs(vertical) ? horizontal : vertical;
          this.moveFocus(Math.sign(dominant));
          this.lastAxisMove = now;
        }
        this.previousGamepadButtons.set(gamepad.index, current);
      }
    }
  }

  private activateFocused(): void {
    const focused = document.activeElement;
    if (focused instanceof HTMLButtonElement) focused.click();
  }

  private moveFocus(delta: number): void {
    const container = this.matchOver
      ? this.resultPanel
      : this.paused
        ? this.pausePanel
        : this.viewport;
    const elements = [...container.querySelectorAll<HTMLElement>('button:not(:disabled), input, select')];
    if (elements.length === 0) return;
    const current = elements.indexOf(document.activeElement as HTMLElement);
    const next = elements[modulo(current + delta, elements.length)];
    if (!next) return;
    next.focus();
    this.markFocus(next);
  }
}
